import { config } from "../config";

// EU country codes
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

// Cache the result to avoid multiple API calls
let euDetectionCache = null;
let euDetectionRefreshPromise = null;

const EU_DETECTION_STORAGE_KEY = 'eu-detection-v1';
const EU_DETECTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const readStoredEUDetection = () => {
  try {
    const raw = localStorage.getItem(EU_DETECTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.is_eu !== 'boolean' || typeof parsed.checked_at !== 'number') {
      return null;
    }

    if (Date.now() - parsed.checked_at > EU_DETECTION_TTL_MS) {
      return null;
    }

    return parsed.is_eu;
  } catch (error) {
    return null;
  }
};

const storeEUDetection = (isEU) => {
  try {
    localStorage.setItem(EU_DETECTION_STORAGE_KEY, JSON.stringify({
      is_eu: isEU,
      checked_at: Date.now(),
    }));
  } catch (error) {
    // Ignore storage failures (e.g. private mode/quota limits)
  }
};

const fetchEUDetection = async () => {
  try {
    // Use a free IP geolocation service
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();

    const isEU = EU_COUNTRIES.includes(data.country_code);
    euDetectionCache = isEU;
    storeEUDetection(isEU);

    console.log('[EU Detection] User country:', data.country_code, 'Is EU:', isEU);
    return isEU;
  } catch (error) {
    console.warn('[EU Detection] Failed to detect user location:', error);
    // Default to showing banner if detection fails (safer for GDPR compliance)
    euDetectionCache = true;
    return true;
  }
};

const refreshEUDetectionInBackground = () => {
  if (euDetectionRefreshPromise) {
    return;
  }

  euDetectionRefreshPromise = fetchEUDetection()
    .catch(() => undefined)
    .finally(() => {
      euDetectionRefreshPromise = null;
    });
};

export const detectEUUser = async () => {
  // Return cached result if available
  if (euDetectionCache !== null) {
    return euDetectionCache;
  }

  // Development flag to force EU detection for testing
  if (config.ENVIRONMENT === 'development' && process.env.REACT_APP_FORCE_EU_TEST === 'true') {
    console.log('[EU Detection] Development mode: Forcing EU detection for testing');
    euDetectionCache = true;
    storeEUDetection(true);
    return true;
  }

  const storedEUDetection = readStoredEUDetection();
  if (storedEUDetection !== null) {
    euDetectionCache = storedEUDetection;
    // Refresh in the background so we keep cached data fresh without delaying startup.
    refreshEUDetectionInBackground();
    return storedEUDetection;
  }

  return fetchEUDetection();
};

// Reset cache (useful for testing)
export const resetEUDetectionCache = () => {
  euDetectionCache = null;
  euDetectionRefreshPromise = null;
  try {
    localStorage.removeItem(EU_DETECTION_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
};

// Reset consent for testing purposes
export const resetConsentForTesting = () => {
  localStorage.removeItem('analytics-consent');
  localStorage.removeItem('consent-preferences-v2');
  localStorage.removeItem('consent-modal-snooze-until');
  resetEUDetectionCache();
  console.log('[EU Detection] Consent reset for testing');
}; 
